import AcademicResultsChart from "@/components/AcademicResultsChart"
import { TooltipWrapper } from "@/components/TooltipWrapper"
import { BlurredContent } from "@/components/BlurredContent"

interface SchoolData {
  name: string
  skolenhetskod: string
  [key: `np_${string}_${string}_${string}`]: string | number
}

export default function TestResults({ schoolData }: { schoolData: SchoolData }) {
  const grades = ["3", "6", "9"]
  const subjects = ["sv", "ma", "en"]
  const years = ["2122", "2223", "2324"]

  const nationalTestAverages = {
    np_3_sv_2122: 93.9,
    np_3_ma_2122: 89.5,
    np_6_sv_2122: 13.3,
    np_6_ma_2122: 12.6,
    np_6_en_2122: 15.9,
    np_9_sv_2122: 13.7,
    np_9_ma_2122: 11.7,
    np_9_en_2122: 16.2,

    np_3_sv_2223: 93,
    np_3_ma_2223: 88,
    np_6_sv_2223: 13.1,
    np_6_ma_2223: 12.1,
    np_6_en_2223: 16.1,
    np_9_sv_2223: 14.1,
    np_9_ma_2223: 12.4,
    np_9_en_2223: 16.3,

    np_3_sv_2324: 93.3,
    np_3_ma_2324: 89.7,
    np_6_sv_2324: 12.8,
    np_6_ma_2324: 11.8,
    np_6_en_2324: 16.5,
    np_9_sv_2324: 13.7,
    np_9_ma_2324: 12.1,
    np_9_en_2324: 16.2,
  }

  const academicResults = grades
    .map((grade) => {
      const gradeData = subjects
        .map((subject) => {
          const subjectData = years
            .map((year) => {
              const key = `np_${grade}_${subject}_${year}`
              const value = schoolData[key] !== "n/a" ? Number.parseFloat(schoolData[key]) : null
              const average = nationalTestAverages[key] || null
              return {
                year,
                value: isNaN(value) ? null : value,
                average: isNaN(average) ? null : average,
              }
            })
            .filter((data) => data.value !== null)

          return {
            subject,
            data: subjectData,
          }
        })
        .filter((subject) => subject.data.length > 0)

      return {
        grade,
        subjects: gradeData,
      }
    })
    .filter((grade) => grade.subjects.length > 0)

  return (
    <div>
      {academicResults.map((grade) => (
        <div key={grade.grade} className="mb-8">
          <h3 className="text-xl font-semibold mt-4 mb-2 flex items-center">
            Nationella prov - årskurs {grade.grade}
            <TooltipWrapper
              content={
                grade.grade === "3"
                  ? "I årskurs 3 genomförs nationella prov i svenska och matematik. Siffran anger andelen elever som har uppnått kunskapskraven. I både svenska och matte genomförs ett antal delprov och det är genomsnittet av delproven som redovisas här."
                  : grade.grade === "6"
                    ? "I årskurs 6 genomförs nationella prov i svenska, matematik och engelska. Resultaten redovisas som genomsnittligt provbetyg där F=0, E=10, D=12.5, C=15, B=17.5 och A=20."
                    : "I årskurs 9 genomförs nationella prov i svenska, matematik och engelska. Resultaten redovisas som genomsnittligt provbetyg där F=0, E=10, D=12.5, C=15, B=17.5 och A=20."
              }
            />
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {grade.subjects.map((subject) => (
              <div key={subject.subject} className="mb-2">
                <h4 className="text-lg font-semibold">
                  {subject.subject === "ma" ? "Matematik" : subject.subject === "sv" ? "Svenska" : "Engelska"}
                </h4>
                {subject.subject === "ma" || subject.subject === "en" ? (
                  <BlurredContent>
                    <AcademicResultsChart
                      data={subject.data}
                      grade={grade.grade}
                      subject={subject.subject}
                      schoolName={schoolData.name}
                      yAxisLabel={grade.grade === "3" ? "Andel elever som nått kunskapskraven" : "Genomsnittlig poäng"}
                    />
                  </BlurredContent>
                ) : (
                  <AcademicResultsChart
                    data={subject.data}
                    grade={grade.grade}
                    subject={subject.subject}
                    schoolName={schoolData.name}
                    yAxisLabel={grade.grade === "3" ? "Andel elever som nått kunskapskraven" : "Genomsnittlig poäng"}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {academicResults.length === 0 && <p>Ingen data tillgänglig för nationella prov.</p>}
    </div>
  )
}

